import React from "react";
import { toast } from "sonner";
import { Loader2, FileText, Download, AlertCircle, CheckCircle2, Clock, XCircle, MessageSquare } from "lucide-react";
import { businessApi, API } from "../../lib/api";

const STATUS_META = {
  billy_created:     { label: "Faktureret", color: "bg-blue-100 text-blue-800", icon: FileText },
  sent_to_customer:  { label: "Afsendt",   color: "bg-amber-100 text-amber-800", icon: Clock },
  paid:              { label: "Betalt",    color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  overdue:           { label: "Forfalden", color: "bg-red-100 text-red-800", icon: AlertCircle },
  disputed:          { label: "Bestridt",  color: "bg-orange-100 text-orange-800", icon: XCircle },
  credited:          { label: "Krediteret", color: "bg-gray-100 text-gray-800", icon: CheckCircle2 },
};

function fmtDKK(v) {
  if (v === null || v === undefined) return "—";
  return new Intl.NumberFormat("da-DK", { style: "currency", currency: "DKK" }).format(v);
}

export default function BusinessBillingPage() {
  const [invoices, setInvoices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(null);
  const [disputeOpen, setDisputeOpen] = React.useState(null); // invoice
  const [disputeReason, setDisputeReason] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await businessApi().get("/b2b/billing/invoices");
      setInvoices(data.items || []);
    } catch (e) {
      toast.error("Kunne ikke hente fakturaer");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const downloadAppendix = async (inv) => {
    try {
      const token = localStorage.getItem("ema_business_token");
      const res = await fetch(`${API}/b2b/billing/invoices/${inv.id}/appendix.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `EMA-bilag-${inv.month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Bilag downloadet");
    } catch (e) {
      toast.error("Kunne ikke downloade bilag");
    }
  };

  const submitDispute = async () => {
    if (!disputeOpen || disputeReason.trim().length < 4) {
      toast.error("Angiv venligst en begrundelse (mindst 4 tegn)");
      return;
    }
    setSubmitting(true);
    try {
      await businessApi().post(`/b2b/billing/invoices/${disputeOpen.id}/dispute`, {
        reason: disputeReason.trim(),
      });
      toast.success("Faktura bestridt — vi vender tilbage hurtigst muligt");
      setDisputeOpen(null);
      setDisputeReason("");
      refresh();
    } catch (e) {
      toast.error("Kunne ikke registrere bestridelse");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="business-billing-page">
      <header>
        <h1 className="text-2xl font-bold text-brand-dark">Fakturaer</h1>
        <p className="text-brand-text2 text-sm">Oversigt over dine fakturaer fra EMA Kurer. Alle beløb er i DKK.</p>
      </header>

      {loading ? (
        <div className="flex items-center gap-2 text-brand-text2"><Loader2 className="animate-spin" size={16}/> Indlæser…</div>
      ) : invoices.length === 0 ? (
        <div className="rounded-lg border border-brand bg-brand-bg2 p-8 text-center text-brand-text2">
          <FileText className="mx-auto mb-3" size={32}/>
          <p>Ingen fakturaer endnu.</p>
          <p className="text-xs mt-1">Fakturaer vises her, når de er afsendt af EMA Kurer.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-brand bg-white">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-brand-bg2 text-brand-text2 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Faktura-nr</th>
                <th className="px-3 py-2 text-left">Periode</th>
                <th className="px-3 py-2 text-right">Pakker</th>
                <th className="px-3 py-2 text-right">Ex moms</th>
                <th className="px-3 py-2 text-right">Moms</th>
                <th className="px-3 py-2 text-right">I alt</th>
                <th className="px-3 py-2 text-left">Forfalden</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => {
                const meta = STATUS_META[inv.workflow_status] || { label: inv.workflow_status, color: "bg-gray-100", icon: FileText };
                const Icon = meta.icon;
                const disputeable = ["billy_created", "sent_to_customer", "overdue"].includes(inv.workflow_status);
                return (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-t border-brand hover:bg-brand-bg2/40">
                    <td className="px-3 py-2 font-mono">{inv.billy_invoice_number || inv.id.slice(0, 8)}</td>
                    <td className="px-3 py-2">{inv.month}</td>
                    <td className="px-3 py-2 text-right">{inv.total_shipments ?? "—"}</td>
                    <td className="px-3 py-2 text-right">{fmtDKK(inv.subtotal_ex_vat_dkk)}</td>
                    <td className="px-3 py-2 text-right">{fmtDKK(inv.vat_dkk)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmtDKK(inv.total_inc_vat_dkk)}</td>
                    <td className="px-3 py-2">{inv.due_date_iso || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${meta.color}`}
                            data-testid={`invoice-status-${inv.id}`}>
                        <Icon size={12}/> {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          data-testid={`invoice-view-${inv.id}`}
                          onClick={() => setSelected(inv)}
                          className="px-2 py-1 text-xs rounded border border-brand hover:bg-brand-bg2"
                        >Vis</button>
                        <button
                          data-testid={`invoice-appendix-${inv.id}`}
                          onClick={() => downloadAppendix(inv)}
                          className="px-2 py-1 text-xs rounded border border-brand hover:bg-brand-bg2 flex items-center gap-1"
                          title="Download bilag (CSV)"
                        ><Download size={12}/> Bilag</button>
                        {disputeable && (
                          <button
                            data-testid={`invoice-dispute-${inv.id}`}
                            onClick={() => setDisputeOpen(inv)}
                            className="px-2 py-1 text-xs rounded border border-orange-300 text-orange-700 hover:bg-orange-50 flex items-center gap-1"
                          ><MessageSquare size={12}/> Bestrid</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <InvoiceDetailModal invoice={selected} onClose={() => setSelected(null)} onDispute={(inv) => { setSelected(null); setDisputeOpen(inv); }} />
      )}

      {disputeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="dispute-modal">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-2">Bestrid faktura</h3>
            <p className="text-sm text-brand-text2 mb-3">Beskriv kort, hvad du mener er forkert. Vi vender tilbage hurtigst muligt.</p>
            <textarea
              data-testid="dispute-reason"
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder="F.eks. forkert antal pakker, manglende rabat, etc."
              className="input-flat w-full mb-3"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setDisputeOpen(null); setDisputeReason(""); }}
                      className="px-4 py-2 rounded border border-brand text-sm">Annullér</button>
              <button onClick={submitDispute} disabled={submitting}
                      data-testid="dispute-submit"
                      className="px-4 py-2 rounded bg-brand-red text-white text-sm font-semibold disabled:opacity-50">
                {submitting && <Loader2 className="inline animate-spin mr-1" size={14}/>}
                Indsend bestridelse
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InvoiceDetailModal({ invoice, onClose, onDispute }) {
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await businessApi().get(`/b2b/billing/invoices/${invoice.id}`);
        setDetail(data);
      } catch {
        toast.error("Kunne ikke hente faktura-detalje");
      } finally { setLoading(false); }
    })();
  }, [invoice.id]);

  const totals = detail?.totals_with_adjustments || detail?.totals || {};
  const meta = STATUS_META[invoice.workflow_status] || { label: invoice.workflow_status, color: "bg-gray-100" };
  const disputeable = ["billy_created", "sent_to_customer", "overdue"].includes(invoice.workflow_status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="invoice-detail-modal">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold">Faktura {detail?.billy_invoice_number || invoice.id.slice(0, 8)}</h3>
            <p className="text-sm text-brand-text2">Periode {invoice.month}</p>
          </div>
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${meta.color}`}>{meta.label}</span>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-brand-text2"><Loader2 className="animate-spin" size={16}/> Indlæser…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div><span className="text-brand-text2">Faktureringsdato:</span> {detail.invoice_date_iso || "—"}</div>
              <div><span className="text-brand-text2">Forfalden:</span> {detail.due_date_iso || "—"}</div>
              <div><span className="text-brand-text2">Betalingsfrist:</span> {detail.payment_terms_days} dage</div>
              <div><span className="text-brand-text2">Pakker i alt:</span> {(detail.totals || {}).total_shipments ?? 0}</div>
            </div>

            <div className="rounded-lg border border-brand bg-brand-bg2/40 p-3 mb-4">
              <div className="text-sm font-semibold mb-2">Beløb</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Subtotal (ex moms)</span><span>{fmtDKK(totals.subtotal_ex_vat_dkk)}</span></div>
                <div className="flex justify-between"><span>Moms ({totals.vat_pct ?? 25}%)</span><span>{fmtDKK(totals.vat_dkk)}</span></div>
                <div className="flex justify-between font-bold border-t border-brand pt-1 mt-1"><span>I alt</span><span>{fmtDKK(totals.total_inc_vat_dkk)}</span></div>
              </div>
            </div>

            {detail.adjustments && detail.adjustments.length > 0 && (
              <div className="mb-4">
                <div className="text-sm font-semibold mb-2">Justeringer</div>
                <table className="w-full text-xs border border-brand rounded">
                  <thead className="bg-brand-bg2 text-brand-text2">
                    <tr><th className="px-2 py-1 text-left">Beskrivelse</th><th className="px-2 py-1 text-right">Antal</th><th className="px-2 py-1 text-right">Beløb</th></tr>
                  </thead>
                  <tbody>
                    {detail.adjustments.map((adj, i) => (
                      <tr key={i} className="border-t border-brand">
                        <td className="px-2 py-1">{adj.label}</td>
                        <td className="px-2 py-1 text-right">{adj.count}</td>
                        <td className="px-2 py-1 text-right">{fmtDKK(adj.total_dkk)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="text-xs text-brand-text2 border-t border-brand pt-3">
              Spørgsmål? Kontakt EMA Kurer på <a className="text-brand-red font-semibold" href="mailto:bogholderi@emakurer.dk">bogholderi@emakurer.dk</a>
            </div>
          </>
        )}
        <div className="flex justify-end gap-2 mt-4">
          {disputeable && !loading && (
            <button onClick={() => onDispute(invoice)} className="px-4 py-2 rounded border border-orange-300 text-orange-700 text-sm">Bestrid</button>
          )}
          <button onClick={onClose} className="px-4 py-2 rounded bg-brand-dark text-white text-sm">Luk</button>
        </div>
      </div>
    </div>
  );
}
